import { createAction, props } from '@ngrx/store';

import { EntityListEntity } from '../components/entity-list/entity-list.entity';

interface IPersistenceInfo {
	isPersisted: boolean;
	partitionCount: number;
	persistenceDate: Date | null;
	persistenceDuration: number;
	persistenceSize: number;
}

export enum EntityActions {
	InitEntityList = '[Entity] get entity list',
	RefreshEntityList = '[BE][Entity] refresh entity list',
	FetchEntityListFail = '[BE][Entity] fetch entity list fail',
	FetchEntityListSuccess = '[BE][Entity] fetch entity list success',
	UpdateEntitiesFrameStatus = '[BE][Entity] get entities frame status',
	UpdateEntitiesFrameStatusSuccess = '[BE][Entity] get entities frame status success',
	UpdateEntitiesFrameStatusFailure = '[BE][Entity] get entities frame status failure',
	ConstructEntityList = '[Entity] construct entity list',
	ConstructEntityListSuccess = '[BE][Entity] construct entity list success',
	ConstructEntityListFail = '[BE][Entity] construct entity list failure',
	NavigateToEntityRoute = '[Entity] navigate to entity route',
	NavigateToEntityCreateRoute = '[Entity] navigate to entity create route',
	NavigateToEntityDuplicateRoute = '[Entity] navigate to entity duplicate route',
	NavigateToCreateFromEntityRoute = '[Entity] navigate to create from entity route',
	PersistEntities = '[Entity] persist entities',
	PersistEntityStarted = '[Entity] persist entity started',
	PersistEntityStopped = '[Entity] persist entity stopped',
	PersistanceActionFinish = '[Entity] persistence action finished',
	PersistEntityFail = '[BE][Entity] persist entity fail',
	PersistenceActionsAccepted = '[Entity] persistence actions accepted',
	TrackEntityAction = '[BE][Entity] track entity action',
	DeleteEntity = '[Entity] delete entity',
	DeleteEntitySuccess = '[BE][Entity] delete entity success',
	DeleteEntityFail = '[BE][Entity] delete entity fail',
	BulkDelete = '[Entity] delete many entities',
	BulkDeleteSuccess = '[BE][Entity] delete many entities success',
	BulkDeleteFail = '[BE][Entity] delete many entities fail',
	RepersistEntities = '[Entity] repersist entities',
	RepersistEntityStarted = '[Entity] repersist entity started',
	RepersistEntityStopped = '[Entity] repersist entity stopped',
	RepersistEntityFail = '[BE][Entity] repersist entity fail',
	UnpersistEntities = '[Entity] unpersist entities',
	UnpersistEntityStarted = '[Entity] unpersist entity started',
	UnpersistEntityStopped = '[Entity] unpersist entity stopped',
	UnpersistEntityFail = '[BE][Entity] unpersist entity fail',
	CreateDatablocksFromEntities = '[Entity] create datablocks from entities',
	CreateDatablocksFromEntitiesSuccess = '[BE][Entity] create datablocks from entities success',
	CreateDatablocksFromEntitiesFail = '[BE][Entity] create datablocks from entities fail',
	ToggleEntityStatus = '[Entity] toggle entity status',
	StopPersistence = '[Entity] stop persistence',
}

export const initEntityList = createAction(EntityActions.InitEntityList);
export const refreshEntityList = createAction(EntityActions.RefreshEntityList);

export const fetchEntityListSuccess = createAction(
	EntityActions.FetchEntityListSuccess,
	props<{ entityList: EntityListEntity }>()
);

export const fetchEntityListFail = createAction(
	EntityActions.FetchEntityListFail,
	props<{ error: unknown }>()
);

export const updateEntitiesFrameStatus = createAction(
	EntityActions.UpdateEntitiesFrameStatus,
	props<{ ids: Array<number>; shouldReload: boolean }>()
);

export const updateEntitiesFrameStatusSuccess = createAction(
	EntityActions.UpdateEntitiesFrameStatusSuccess,
	props<{ entityList: EntityListEntity }>()
);

export const updateEntitiesFrameStatusFailure = createAction(
	EntityActions.UpdateEntitiesFrameStatusFailure,
	props<{ error: unknown }>()
);

export const constructEntityList = createAction(
	EntityActions.ConstructEntityList
);

export const constructEntityListSuccess = createAction(
	EntityActions.ConstructEntityListSuccess,
	props<{ entityList: EntityListEntity }>()
);

export const constructEntityListFail = createAction(
	EntityActions.ConstructEntityListFail,
	props<{ error: unknown }>()
);

export const navigateToEntity = createAction(
	EntityActions.NavigateToEntityRoute,
	props<{ id: number }>()
);
export const navigateToEntityCreate = createAction(
	EntityActions.NavigateToEntityCreateRoute
);
export const navigateToEntityDuplicate = createAction(
	EntityActions.NavigateToEntityDuplicateRoute,
	props<{ id: number }>()
);
export const navigateToCreateFromEntity = createAction(
	EntityActions.NavigateToCreateFromEntityRoute,
	props<{ id: number }>()
);

export const persistEntities = createAction(
	EntityActions.PersistEntities,
	props<{ entityIds: Array<number> }>()
);

export const persistEntityStarted = createAction(
	EntityActions.PersistEntityStarted,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const persistEntityStopped = createAction(
	EntityActions.PersistEntityStopped,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const persistEntityFail = createAction(
	EntityActions.PersistEntityFail,
	props<{ error: unknown }>()
);

export const persistenceActionFinish = createAction(
	EntityActions.PersistanceActionFinish,
	props<{
		entityId: number;
		persistenceInfo: IPersistenceInfo;
	}>()
);

export const persistenceActionsAccepted = createAction(
	EntityActions.PersistenceActionsAccepted
);

export const trackAction = createAction(
	EntityActions.TrackEntityAction,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const deleteEntity = createAction(
	EntityActions.DeleteEntity,
	props<{ entityId: number }>()
);

export const bulkDelete = createAction(
	EntityActions.BulkDelete,
	props<{ entityId: Array<number> }>()
);

export const deleteEntitySuccess = createAction(
	EntityActions.DeleteEntitySuccess
);

export const deleteEntityFail = createAction(EntityActions.DeleteEntityFail);

export const bulkDeleteSuccess = createAction(EntityActions.BulkDeleteSuccess);

export const bulkDeleteFail = createAction(EntityActions.BulkDeleteFail);

export const repersistEntities = createAction(
	EntityActions.RepersistEntities,
	props<{ entityIds: Array<number> }>()
);

export const repersistEntityStarted = createAction(
	EntityActions.RepersistEntityStarted,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const repersistEntityStopped = createAction(
	EntityActions.RepersistEntityStopped,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const repersistEntityFail = createAction(
	EntityActions.RepersistEntityFail,
	props<{ error: unknown }>()
);

export const unpersistEntities = createAction(
	EntityActions.UnpersistEntities,
	props<{ entityIds: Array<number> }>()
);

export const unpersistEntityStarted = createAction(
	EntityActions.UnpersistEntityStarted,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const unpersistEntityStopped = createAction(
	EntityActions.UnpersistEntityStopped,
	props<{
		entityId: number;
		jobId: string;
	}>()
);

export const unpersistEntityFail = createAction(
	EntityActions.UnpersistEntityFail,
	props<{ error: unknown }>()
);

export const createDatablocksFromEntities = createAction(
	EntityActions.CreateDatablocksFromEntities,
	props<{ entityId: Array<number> }>()
);

export const createDatablocksFromEntitiesSuccess = createAction(
	EntityActions.CreateDatablocksFromEntitiesSuccess
);

export const createDatablocksFromEntitiesFail = createAction(
	EntityActions.CreateDatablocksFromEntitiesFail
);

export const toggleEntityStatus = createAction(
	EntityActions.ToggleEntityStatus,
	props<{ entityId: number }>()
);

export const stopPersistence = createAction(
	EntityActions.StopPersistence,
	props<{ jobKey: string }>()
);
